import { shouldAnimate, transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { siteWide } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { headingStyles, paragraphStyles } from '@helpers/typography';
import LinkButton from '@stories/Components/Buttons/LinkButton/LinkButton';
import ReviewScoreStyles from '@stories/Components/Misc/ReviewScore/ReviewScore.styles';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
const Container = styled(SectionWrapper) `
  --bgColor: ${brand.white};
  --fgColor: ${brand.white};
  --topSpacing: calc(var(--sectionSpacing) - 25px);

  position: relative;
  background-color: var(--bgColor);
  color: var(--fgColor);
  margin-bottom: 0;
  height: calc(100svh - var(--headerNavHeight));
  max-height: calc(731px - var(--headerNavHeight));
  margin-top: 0;

  &[data-theme='dark'] {
    --bgColor: ${brand.black};
    --fgColor: ${brand.white};

    margin: 0;
    scroll-margin: var(--scrollOffset) 0 0;
  }

  @media ${from(Device.Tablet)} {
    min-height: 800px;
    height: auto;

    &[data-theme='dark'] {
      padding-top: var(--topSpacing);
      margin: 0;
    }
  }

  @media ${until(Device.Tablet)} {
    display: grid;
    align-items: flex-end;
    margin: 0;
  }
`;
const ContentWrapper = styled.div `
  ${siteWide}

  max-width: 768px;
  margin: 0;
  position: relative;
  display: flex;
  align-items: flex-start;
  column-gap: ${fluid(16, 32)};
  flex-direction: column;
  row-gap: 20px;
  z-index: 1;
  padding-bottom: 40px;
  text-wrap: balance;

  @media ${from(Device.Tablet)} {
    padding-top: 278px;
  }

  @media (max-height: 880px) and (min-width: 768px) {
    padding-top: 208px;
  }

  @media ${from(Device.TabletLarge)} {
    max-width: 950px;
  }
  
`;
const Title = styled.h1 `
  ${headingStyles}

  ${transition('opacity')}
  transition-delay: 0.45s;

  display: block;
  font-size: ${fluid(58, 110, Device.TabletSmall, 1440)};
  line-height: ${fluid(58, 105, Device.TabletSmall, 1440)};
  margin: 0;
  text-wrap: balance;
  margin-bottom: -5px;
  max-width: 24ch;

  u {
    text-decoration-color: ${brand.yellow};
    text-decoration-thickness: max(6px, 12 / 140 * 1em);
  }

  ${shouldAnimate(css `
    ${Container}[data-animate="false"] & {
      opacity: 0;
    }

    ${Container}[data-animate="true"] & {
      opacity: 1;
    }
  `)}
`;
const Content = styled.p `
  ${paragraphStyles}

  ${transition('opacity')}
  transition-delay: 0.6s;

  display: block;
  font-size: ${fluid(18, 23)};
  line-height: ${fluid(28, 31)};
  margin: 0;
  margin-bottom: 10px;

  ${shouldAnimate(css `
    ${Container}[data-animate="false"] & {
      opacity: 0;
    }

    ${Container}[data-animate="true"] & {
      opacity: 1;
    }
  `)}

  @media ${from(Device.Tablet)} {
    max-width: none;
    margin-bottom: 24px;
  }
`;
const CtaWrapper = styled.div `
  ${transition('opacity')}
  transition-delay: 0.6s;

  display: block;
  margin-top: 12px;

  @media (hover: hover) {
    display: none;
  }

  ${shouldAnimate(css `
    ${Container}[data-animate="false"] & {
      opacity: 0;
    }

    ${Container}[data-animate="true"] & {
      opacity: 1;
    }
  `)}
`;
const MediaWrapper = styled.div `
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    background: linear-gradient(to bottom, ${rgba(brand.black, 0)}, ${rgba(brand.black, 1)});
    height: 100%;
    opacity: 0.53;
    width: 100%;
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    background: ${brand.black};
    height: 100%;
    opacity: 0.37;
    width: 100%;
    z-index: 1;
  }
`;
const Video = styled.video `
  display: block;
  height: 100%;
  object-fit: cover;
  object-position: center;
  width: 100%;

  @media ${from(Device.Tablet)} {
    position: absolute;
    top: 0;
    left: 0;
  }
`;
const Image = styled.img `
  display: block;
  height: 100%;
  object-fit: cover;
  object-position: center;
  width: 100%;

  @media ${from(Device.Tablet)} {
    position: absolute;
    top: 0;
    left: 0;
  }
`;
const Actions = styled.div `
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;

  @media ${from(Device.Tablet)} {
    flex-direction: row;
  }
`;
const CTA = styled(LinkButton) `
  width: 100%;
  height: 55px;

  span {
    margin-inline: auto;
  }

  @media ${from(Device.Tablet)} {
    width: auto;
    padding-inline: 30px;
  }
`;
const SearchCTA = styled(CTA) `
  &[data-type='plain'] {
    --colorFgActive: ${brand.yellow};
  }
`;
const ReviewScoreWrapper = styled.div `
  margin-bottom: 16px;

  ${ReviewScoreStyles.RatingText} {
    font-weight: 400;
  }

  ${ReviewScoreStyles.StarContainer} {
    gap: 2px;
  }

  ${ReviewScoreStyles.TextContainer} {
    font-size: 16px;
    align-items: flex-end;

    svg {
      width: 109px;
      height: 22px;
    }
  }

  @media ${until(Device.Tablet)} {
    ${ReviewScoreStyles.StarContainer} {
      svg {
        width: 18px;
        height: 18px;
      }
    }
  }

  @media ${from(Device.Tablet)} {
    margin-bottom: 30px;
  }
`;
const DiscoverCTA = styled(CTA) ``;
const HomepageHeroBannerStyles = {
    Container,
    Content,
    ContentWrapper,
    CtaWrapper,
    Image,
    MediaWrapper,
    ReviewScoreWrapper,
    SearchCTA,
    DiscoverCTA,
    Actions,
    Title,
    Video,
};
export default HomepageHeroBannerStyles;
